import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!token || !user) {
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.getPermissions();
      setPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // Set default permissions based on role
      const defaultPerms = getDefaultPermissions(user.role);
      setPermissions(defaultPerms);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = (permission) => {
    // Admin always has all permissions
    if (user?.role === 'Admin') return true;
    return permissions[permission] === true;
  };

  const canCreate = (resource) => {
    const permMap = {
      roles: 'create_roles',
      cvs: 'upload_cvs',
      teams: 'create_teams',
      feedback: 'add_feedback',
      sops: 'manage_sops',
      users: 'manage_users'
    };
    return hasPermission(permMap[resource]);
  };

  const canEdit = (resource) => {
    const permMap = {
      roles: 'edit_roles',
      cvs: 'edit_cvs',
      teams: 'edit_teams',
      feedback: 'edit_feedback',
      sops: 'manage_sops',
      users: 'manage_users'
    };
    return hasPermission(permMap[resource]);
  };



  const canView = (resource) => {
    const permMap = {
      roles: 'view_roles',
      cvs: 'view_cvs',
      teams: 'view_teams',
      feedback: 'view_feedback',
      sops: 'view_sops',
      users: 'view_users',
      dashboard: 'view_dashboard',
      reports: 'view_reports'
    };
    return hasPermission(permMap[resource]);
  };
// Manually cv delete permission
const canDelete = (resource) => {
  if (user?.role === 'Admin' || user?.role === 'Director' || user?.role === 'Account Manager') {
    if (resource === 'cvs') return true;
    return canEdit(resource);
  }
  return false;
};


  return (
    <PermissionsContext.Provider value={{ 
      permissions, 
      loading, 
      hasPermission, 
      canCreate, 
      canEdit, 
      canView,
      refreshPermissions: fetchPermissions 
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

// Default permissions based on role (fallback)
function getDefaultPermissions(role) {
  switch (role) {
    case 'Admin':
      return {
        view_roles: true, create_roles: true, edit_roles: true, close_roles: true,
        view_cvs: true, upload_cvs: true, edit_cvs: true,
        view_teams: true, create_teams: true, edit_teams: true,
        view_feedback: true, add_feedback: true, edit_feedback: true,
        view_users: true, manage_users: true,
        view_sops: true, manage_sops: true,
        view_dashboard: true, view_reports: true, export_reports: true
      };
    case 'Director':
    case 'Account Manager':
      return {
        view_roles: true, create_roles: true, edit_roles: true, close_roles: true,
        view_cvs: true, upload_cvs: true, edit_cvs: true,
        view_teams: true, create_teams: true, edit_teams: true,
        view_feedback: true, add_feedback: true, edit_feedback: true,
        view_users: true, manage_users: true,
        view_sops: true, manage_sops: true,
        view_dashboard: true, view_reports: true, export_reports: true
      };
    case 'Recruiter':
      return {
        view_roles: true, create_roles: false, edit_roles: false, close_roles: false,
        view_cvs: true, upload_cvs: true, edit_cvs: true,
        view_teams: true, create_teams: false, edit_teams: false,
        view_feedback: true, add_feedback: true, edit_feedback: true,
        view_users: false, manage_users: false,
        view_sops: true, manage_sops: false,
        view_dashboard: true, view_reports: true, export_reports: false
      };
    case 'Trainee':
    default:
      return {
        view_roles: true, create_roles: false, edit_roles: false, close_roles: false,
        view_cvs: true, upload_cvs: false, edit_cvs: false,
        view_teams: true, create_teams: false, edit_teams: false,
        view_feedback: true, add_feedback: false, edit_feedback: false,
        view_users: false, manage_users: false,
        view_sops: true, manage_sops: false,
        view_dashboard: true, view_reports: true, export_reports: false
      };
  }
}
